import { planMissions } from './missions.js';
const BAND_WORDS = {
    POOR: 'Poor',
    FAIR: 'Fair',
    GOOD: 'Good',
    VERY_GOOD: 'Very good',
    EXCELLENT: 'Excellent',
};
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const short = (s, n = 70) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const day = (iso) => new Date(iso).toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kuala_Lumpur',
});
const OPEN_CONFLICT = new Set(['open', 'disputed']);
const DECLARED = new Set(['USER_DECLARED', 'INFERRED', 'AI_EXTRACTED']);
/**
 * What an underwriter or broker should know first: what caps or blocks the score, answers the evidence
 * contradicts, controls only declared, what is waiting on someone, and the biggest single lift.
 */
export function staffInsights(s, limit = 3) {
    const out = [];
    const r = s.score?.result;
    const trace = s.score?.trace;
    if (r) {
        const failed = r.kill_switches.failed;
        if (failed.length && r.kill_switches.cap !== null) {
            const worst = [...failed].sort((a, b) => a.cap - b.cap)[0];
            out.push({
                id: `cap:${worst.control}`,
                tone: 'bad',
                title: `Score capped at ${r.kill_switches.cap}`,
                text: failed.length === 1
                    ? `${worst.name} is not in place, so the score can’t go above ${worst.cap} until it is.`
                    : `${failed.length} kill switches failed, the lowest cap from ${worst.name}.`,
                source: { label: `Kill switch ${worst.control}`, target: { to: 'score' } },
                action: { label: 'See what lifts it', target: { to: 'score' } },
                weight: 100,
            });
        }
        if (r.profile?.below_minimum && r.profile.minimum_band)
            out.push({
                id: 'below-minimum',
                tone: 'bad',
                title: `Below the ${r.profile.name.toLowerCase()} minimum`,
                text: `${r.profile.name} needs ${BAND_WORDS[r.profile.minimum_band]} or better; this is ${BAND_WORDS[r.band]}, so it is referred before cover is offered.`,
                source: { label: `Industry profile: ${r.profile.name}`, target: { to: 'score' } },
                action: { label: 'Open the score', target: { to: 'score' } },
                weight: 95,
            });
        const pending = r.kill_switches.pending;
        if (pending.length && trace) {
            const first = trace.controls.find((c) => pending.includes(c.control));
            out.push({
                id: 'pending-kill-switches',
                tone: 'warn',
                title: `${plural(pending.length, 'kill-switch question')} open`,
                text: 'Not answered, or answered “not sure”: a cap could still apply once they are.',
                source: {
                    label: first ? `${first.control} ${first.name}` : 'Kill switches',
                    target: { to: 'score' },
                },
                action: { label: 'Open the questions', target: { to: 'answer', question: first?.questions[0] } },
                weight: 85,
            });
        }
    }
    const conflicts = (s.conflicts ?? []).filter((c) => OPEN_CONFLICT.has(c.state));
    if (conflicts.length) {
        const c = [...conflicts].sort((a, b) => Number(b.kill_switch) - Number(a.kill_switch))[0];
        out.push({
            id: `conflict:${c.id}`,
            tone: c.kill_switch ? 'bad' : 'warn',
            title: conflicts.length === 1 ? 'Answer contradicted' : `${conflicts.length} answers contradicted`,
            text: `${short(c.question_text, 60)}: answered “${c.answer.label}”, but ${c.reading.reason.replace(/\.$/, '')}.`,
            source: { label: `${c.source_name}, ${day(c.observed_at)}`, target: { to: 'conflicts' } },
            action: {
                label: c.state === 'disputed' ? 'Decide the dispute' : 'Review conflict',
                target: { to: 'conflicts' },
            },
            weight: c.kill_switch ? 90 : 80,
        });
    }
    if (trace) {
        const declared = trace.controls.filter((c) => c.status === 'in_place' && (!c.provenance || DECLARED.has(c.provenance)) && !c.checked);
        if (declared.length) {
            const top = [...declared].sort((a, b) => b.weight - a.weight || (a.control < b.control ? -1 : 1))[0];
            out.push({
                id: 'declared-not-verified',
                tone: 'info',
                title: `${plural(declared.length, 'control')} declared, not verified`,
                text: `The weightiest is ${top.name}${top.kill_switch ? ', a kill switch' : ''}. A document or connection would back it.`,
                source: { label: `Answer ${top.answered[0] ?? top.questions[0] ?? top.control}`, target: { to: 'answer', question: top.answered[0] ?? top.questions[0] } }, // prettier-ignore
                action: { label: 'Ask for evidence', target: { to: 'documents' } },
                weight: 60 + Math.min(15, Math.round(top.weight * 100)),
            });
        }
    }
    const waiting = (s.follow_ups ?? []).filter((f) => f.state === 'open' || f.state === 'answered');
    if (waiting.length) {
        const answered = waiting.filter((f) => f.state === 'answered');
        const f = answered[0] ?? waiting[0];
        out.push({
            id: `follow-up:${f.id}`,
            tone: 'warn',
            title: answered.length ? `${plural(answered.length, 'follow-up reply', 'follow-up replies')} to read` : `${plural(waiting.length, 'follow-up')} waiting on the business`, // prettier-ignore
            text: short(f.prompt, 110),
            source: { label: `Question ${f.question_key}`, target: { to: 'answer', question: f.question_key } },
            action: { label: answered.length ? 'Read the reply' : 'See follow-ups', target: { to: 'follow_ups' } },
            weight: answered.length ? 75 : 55,
        });
    }
    const weak = (s.outside_in?.factors ?? []).filter((f) => f.grade === 'D' || f.grade === 'F');
    if (s.outside_in && weak.length) {
        const f = [...weak].sort((a, b) => (a.score ?? 100) - (b.score ?? 100))[0];
        out.push({
            id: `outside-in:${f.key}`,
            tone: f.grade === 'F' ? 'warn' : 'info',
            title: `${f.name}: grade ${f.grade} from the internet`,
            text: f.issues[0] ? short(f.issues[0], 110) : 'Seen from outside the business, not part of the score.',
            source: { label: `Internet check, ${day(s.outside_in.as_of)}`, target: { to: 'outside_in' } },
            action: { label: 'Open finding', target: { to: 'outside_in' } },
            weight: f.grade === 'F' ? 70 : 50,
        });
    }
    if (r?.provisional)
        out.push({
            id: 'provisional',
            tone: 'info',
            title: 'Score still provisional',
            text: `${r.controls.applicable - r.controls.answered} of ${r.controls.applicable} applicable controls have no answer yet, so it can still move a lot.`,
            source: { label: 'CyberScore', target: { to: 'score' } },
            action: { label: 'Open the questions', target: { to: 'answer' } },
            weight: 45,
        });
    const lift = trace?.actions[0];
    if (lift && lift.points > 0)
        out.push({
            id: `lift:${lift.control}`,
            tone: 'info',
            title: `Biggest lift: ${lift.name}`,
            text: `+${lift.points} points if fully in place${lift.lifts_cap ? ', and it lifts the cap' : ''}.`,
            source: { label: `Control ${lift.control}`, target: { to: 'score' } },
            action: { label: 'See the plan', target: { to: 'score' } },
            weight: 40,
        });
    return rank(out, limit);
}
/**
 * What a business owner should do next, in their words: what holds the score back, what their insurer or
 * broker asked, and the missions worth most points.
 */
export function ownerInsights(s, limit = 3) {
    const out = [];
    const r = s.score?.result;
    const trace = s.score?.trace;
    const plans = trace ? planMissions(trace) : [];
    if (r && r.kill_switches.failed.length && r.kill_switches.cap !== null) {
        const worst = [...r.kill_switches.failed].sort((a, b) => a.cap - b.cap)[0];
        const mission = plans.find((p) => p.next.steps.some((st) => st.control === worst.control));
        out.push({
            id: `cap:${worst.control}`,
            tone: 'bad',
            title: `${worst.name} is holding your score back`,
            text: `Your score can’t go above ${worst.cap} until it’s in place.`,
            source: { label: 'Your CyberScore', target: { to: 'score' } },
            action: { label: 'Fix it', target: { to: 'mission', mission: mission?.id } },
            weight: 100,
        });
    }
    const asked = (s.follow_ups ?? []).filter((f) => f.state === 'open');
    if (asked.length) {
        const f = asked[0];
        out.push({
            id: `follow-up:${f.id}`,
            tone: 'warn',
            title: asked.length === 1 ? 'Your insurer asked a question' : `Your insurer asked ${asked.length} questions`,
            text: short(f.prompt, 110),
            source: {
                label: `About: ${short(f.question_text, 48)}`,
                target: { to: 'answer', question: f.question_key },
            },
            action: { label: 'Reply', target: { to: 'answer', question: f.question_key } },
            weight: 90,
        });
    }
    const contradicted = (s.conflicts ?? []).filter((c) => c.state === 'open');
    if (contradicted.length) {
        const c = contradicted[0];
        out.push({
            id: `conflict:${c.id}`,
            tone: 'warn',
            title: 'An answer doesn’t match what we found',
            text: `${short(c.question_text, 60)}: ${c.reading.reason.replace(/\.$/, '')}.`,
            source: {
                label: `${c.source_name}, ${day(c.observed_at)}`,
                target: { to: 'answer', question: c.question },
            },
            action: { label: 'Check your answer', target: { to: 'answer', question: c.question } },
            weight: 80,
        });
    }
    const missions = plans
        .filter((p) => p.next.points > 0 && p.next.steps.length)
        .sort((a, b) => b.next.points - a.next.points || (a.id < b.id ? -1 : 1));
    for (const [i, m] of missions.slice(0, 2).entries())
        out.push({
            id: `mission:${m.id}`,
            tone: 'info',
            title: m.next.steps[0].name,
            text: `${m.name}: worth +${m.next.points} points${m.lifts_cap ? ' and lifts the cap on your score' : ''}.`,
            source: { label: `Mission: ${m.name}`, target: { to: 'mission', mission: m.id } },
            action: { label: `Start · +${m.next.points} pts`, target: { to: 'mission', mission: m.id } },
            weight: 60 - i,
        });
    return rank(out, limit);
}
/**
 * The owner's next steps in order (task 103, decision 0074): the first is the one thing their home asks
 * them to do, the rest wait behind "more". Where the assessment stands comes first, because finishing what
 * is open gives them closure: changes their insurer asked for, their insurer's questions, required
 * questions still open, then getting the report once they're all answered. Only then what to fix, in the
 * order ownerInsights already ranks it.
 */
export function ownerNextSteps(s, status) {
    const r = s.score?.result;
    const out = ownerInsights(s, Number.POSITIVE_INFINITY);
    const check = { to: 'answer' };
    const ch = s.changes;
    if (status === 'in_remediation')
        out.push(ch && ch.asked > 0 && ch.left === 0
            ? {
                id: 'changes',
                tone: 'info',
                title: 'Send your changes back',
                text: 'Every change is done. Send it back so your insurer can finish their review.',
                source: { label: 'Your insurer’s review', target: check },
                action: { label: 'Send it back', target: check },
                weight: 0,
            }
            : {
                id: 'changes',
                tone: 'warn',
                title: ch?.asked
                    ? `Your insurer asked for ${plural(ch.asked, 'change')}`
                    : 'Your insurer asked for changes',
                text: 'Change the answers they asked about, or reply to say why they’re right, then send it back.',
                source: { label: 'Your insurer’s review', target: check },
                action: {
                    label: ch && ch.left < ch.asked ? `Do the last ${ch.left}` : 'See the changes',
                    target: check,
                },
                weight: 0,
            });
    if (status === 'in_progress' && (!r || r.provisional))
        out.push({
            id: 'check',
            tone: 'info',
            title: r ? 'Finish your Cyber Check' : 'Start your Cyber Check',
            text: r
                ? 'Some required questions are still open, so your score is provisional until they’re answered.'
                : 'About 30 minutes of plain questions about how your business works. Your score builds as you go.',
            source: { label: 'Your Cyber Check', target: check },
            action: { label: r ? 'Continue Cyber Check' : 'Start Cyber Check', target: check },
            weight: 0,
        });
    if (status === 'in_progress' && r && !r.provisional)
        out.push({
            id: 'submit',
            tone: 'info',
            title: 'Get your report',
            text: 'Every required question is answered. Finish to get your report and send it to your insurer.',
            source: { label: 'Your Cyber Check', target: check },
            action: { label: 'Finish and get my report', target: check },
            weight: 0,
        });
    const at = (x) => NEXT_ORDER.indexOf(x.id.split(':')[0]);
    return out.sort((a, b) => at(a) - at(b) || b.weight - a.weight || (a.id < b.id ? -1 : 1));
}
const NEXT_ORDER = ['changes', 'follow-up', 'check', 'submit', 'cap', 'conflict', 'mission'];
const DAY = 86_400_000;
const list = (xs, n = 3) => xs.length <= n ? xs.join(', ') : `${xs.slice(0, n).join(', ')} and ${xs.length - n} more`;
/**
 * Configuration checks for an organisation's administrators: what would quietly give a wrong score or a
 * form that can't be sent (controls nothing scores, an insurer's blank PDF missing or of another edition), a
 * release waiting on someone, and access going stale (invitations never accepted, API keys about to stop).
 * Questions without a control are not flagged: profile and context questions are unscored on purpose.
 */
export function tenantInsights(s, limit = 3) {
    const out = [];
    if (s.questions && s.controls) {
        const scored = new Set(s.questions.flatMap((q) => q.body.metadata?.controls ?? []));
        const live = new Map();
        for (const c of s.controls)
            if (!c.retired_at && !live.has(c.key))
                live.set(c.key, c.title);
        const orphans = [...live].filter(([k]) => !scored.has(k)).sort(([a], [b]) => (a < b ? -1 : 1));
        if (orphans.length)
            out.push({
                id: 'controls-unscored',
                tone: 'warn',
                title: `${plural(orphans.length, 'control')} no question answers`,
                text: `Every business is scored without ${orphans.length === 1 ? 'it' : 'them'}: ${list(orphans.map(([k, t]) => `${k} ${short(t, 40)}`), 2)}.`,
                source: { label: 'Studio: controls', target: 'journeys' },
                action: { label: 'Open the studio', target: 'journeys' },
                weight: 80,
            });
    }
    if (s.pending_release)
        out.push({
            id: 'release-pending',
            tone: 'info',
            title: 'A release is waiting for approval',
            text: `${s.pending_release.submitted_by_name ?? 'Someone'} submitted it on ${day(s.pending_release.submitted_at)}${s.pending_release.note ? `: “${short(s.pending_release.note, 60)}”` : ''}.`,
            source: { label: 'Releases', target: 'releases' },
            action: { label: 'Review it', target: 'releases' },
            weight: 85,
        });
    const forms = s.insurer_forms ?? [];
    const wrong = forms.filter((f) => f.pdf && f.template && f.pdf.sha256 !== f.template.sha256);
    const blank = forms.filter((f) => f.pdf && !f.template);
    const unmapped = forms.filter((f) => !f.pdf);
    if (wrong.length)
        out.push({
            id: 'forms-edition',
            tone: 'bad',
            title: `${plural(wrong.length, 'insurer form')} ${wrong.length === 1 ? 'has' : 'have'} another edition uploaded`,
            text: `The blank PDF isn’t the edition the fields were mapped on, so it can’t be filled: ${list(wrong.map((f) => f.form_code))}.`,
            source: { label: 'Insurer forms', target: 'insurer_forms' },
            action: { label: 'Upload the right one', target: 'insurer_forms' },
            weight: 90,
        });
    if (blank.length)
        out.push({
            id: 'forms-blank',
            tone: 'warn',
            title: `${plural(blank.length, 'insurer form')} can’t be printed yet`,
            text: `Upload the insurer’s blank PDF for ${list(blank.map((f) => f.form_code))}; until then brokers get the answers but not the form.`,
            source: { label: 'Insurer forms', target: 'insurer_forms' },
            action: { label: 'Upload blanks', target: 'insurer_forms' },
            weight: 70,
        });
    if (unmapped.length)
        out.push({
            id: 'forms-unmapped',
            tone: 'info',
            title: `${plural(unmapped.length, 'insurer form')} ${unmapped.length === 1 ? 'has' : 'have'} no PDF mapping`,
            text: `${list(unmapped.map((f) => f.form_code))}: the answers are filled in, but not onto the insurer’s own PDF.`,
            source: { label: 'Insurer forms', target: 'insurer_forms' },
            action: { label: 'See the forms', target: 'insurer_forms' },
            weight: 30,
        });
    const invites = s.invitations ?? [];
    const expired = invites.filter((i) => i.expired || Date.parse(i.expires_at) <= s.now);
    const waiting = invites.filter((i) => !expired.includes(i) && s.now - Date.parse(i.created_at) > 7 * DAY);
    if (expired.length)
        out.push({
            id: 'invites-expired',
            tone: 'warn',
            title: `${plural(expired.length, 'invitation')} expired unused`,
            text: `${list(expired.map((i) => i.email), 2)} never joined. Invite them again or revoke ${expired.length === 1 ? 'it' : 'them'}.`,
            source: { label: 'People', target: 'people' },
            action: { label: 'See invitations', target: 'people' },
            weight: 60,
        });
    if (waiting.length)
        out.push({
            id: 'invites-waiting',
            tone: 'info',
            title: `${plural(waiting.length, 'invitation')} not accepted after a week`,
            text: `${list(waiting.map((i) => i.email), 2)} ${waiting.length === 1 ? 'hasn’t' : 'haven’t'} signed in yet.`,
            source: { label: 'People', target: 'people' },
            action: { label: 'See invitations', target: 'people' },
            weight: 40,
        });
    const keys = (s.api_keys ?? []).filter((k) => !k.revoked_at);
    const dead = keys.filter((k) => k.expires_at && Date.parse(k.expires_at) <= s.now);
    const soon = keys
        .filter((k) => k.expires_at && Date.parse(k.expires_at) > s.now && Date.parse(k.expires_at) - s.now <= 30 * DAY)
        .sort((a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at));
    const idle = keys.filter((k) => !dead.includes(k) && s.now - Date.parse(k.last_used_at ?? k.created_at) > 90 * DAY);
    if (soon.length) {
        const days = Math.ceil((Date.parse(soon[0].expires_at) - s.now) / DAY);
        out.push({
            id: 'keys-expiring',
            tone: 'warn',
            title: soon.length === 1
                ? `API key “${short(soon[0].name, 30)}” stops working in ${plural(days, 'day')}`
                : `${soon.length} API keys stop working within 30 days`,
            text: 'Whatever uses it stops getting data. Create its replacement before then.',
            source: { label: 'API and webhooks', target: 'integrations' },
            action: { label: 'See keys', target: 'integrations' },
            weight: 75,
        });
    }
    if (dead.length)
        out.push({
            id: 'keys-expired',
            tone: 'info',
            title: `${plural(dead.length, 'API key')} expired`,
            text: `${list(dead.map((k) => `“${short(k.name, 30)}”`), 2)} no longer work${dead.length === 1 ? 's' : ''}; revoke ${dead.length === 1 ? 'it' : 'them'} to tidy the list.`,
            source: { label: 'API and webhooks', target: 'integrations' },
            action: { label: 'See keys', target: 'integrations' },
            weight: 35,
        });
    if (idle.length)
        out.push({
            id: 'keys-idle',
            tone: 'info',
            title: `${plural(idle.length, 'API key')} unused for 90 days`,
            text: `A key nobody uses is access nobody needs: ${list(idle.map((k) => `“${short(k.name, 30)}”`), 2)}.`,
            source: { label: 'API and webhooks', target: 'integrations' },
            action: { label: 'Revoke unused keys', target: 'integrations' },
            weight: 45,
        });
    return rank(out, limit);
}
function rank(list, limit) {
    return [...list].sort((a, b) => b.weight - a.weight || (a.id < b.id ? -1 : 1)).slice(0, limit);
}
//# sourceMappingURL=insights.js.map